'use client';

import {
  getClientApplicationConfig,
  getClientApplicationContext,
} from '@hc-frontend/core-context';
import { useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import type {
  TrackClickUseCase,
  TrackImpressionUseCase,
} from '@hc-frontend/features-ads';
import {
  semContentAdapterFactory,
  trackClickFactory,
  trackImpressionFactory,
} from '@hc-frontend/features-ads';
import type {
  TrackSubmitPersonalInformationPlateUseCase,
  TrackSubmitZipPlateUseCase,
} from '@hc-frontend/features-tracking';
import {
  trackSubmitPersonalInformationPlateAdapterFactory,
  trackSubmitPersonalInformationPlateFactory,
  trackSubmitZipPlateAdapterFactory,
  trackSubmitZipPlateFactory,
} from '@hc-frontend/features-tracking';

export interface CompositionRoot {
  trackAdImpression: TrackImpressionUseCase;
  trackAdClick: TrackClickUseCase;
  trackZipPlateSubmission: TrackSubmitZipPlateUseCase;
  trackSubmitPersonalInformationPlate: TrackSubmitPersonalInformationPlateUseCase;
}

let root: CompositionRoot;

export function useCompositionRoot() {
  const eventBus = useEventBus();
  const trackingContexts = useTrackingContexts();

  if (root) {
    return root;
  }

  const config = getClientApplicationConfig();
  const { log } = getClientApplicationContext(null);

  const semContentRepository = semContentAdapterFactory({
    baseUrl: config.sem.apiURL,
  });
  const trackAdImpression = trackImpressionFactory(semContentRepository);

  const trackAdClick = trackClickFactory(semContentRepository);

  const { trackSubmitZipPlate } = trackSubmitZipPlateAdapterFactory({
    eventBus,
    trackingContexts,
  });
  const trackZipPlateSubmission = trackSubmitZipPlateFactory({
    trackSubmitZipPlate,
    log,
  });

  const {
    trackSubmitPersonalInformationPlate: trackSubmitPersonalInformationAdapter,
  } = trackSubmitPersonalInformationPlateAdapterFactory({
    eventBus,
    trackingContexts,
  });
  const trackSubmitPersonalInformationPlate =
    trackSubmitPersonalInformationPlateFactory({
      trackSubmitPersonalInformationPlate:
        trackSubmitPersonalInformationAdapter,
      log,
    });

  root = {
    trackZipPlateSubmission,
    trackSubmitPersonalInformationPlate,
    trackAdImpression,
    trackAdClick,
  };

  return root;
}
